import React, { useState, useMemo } from 'react';
import Button from '../Button';
import FontAwesomeIcon from '../Icon';
import Overlay from '../Overlay';
import Search from '../Search';
import Actions from '../Actions';
import Empty from '../Empty';
import { getUrlExtension } from 'src/helpers/CommonHelpers';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import { filterNotesBySearchTerm } from 'src/components/NotesNewWithState/helpers';
import NoteFormWithState from 'src/components/NoteFormNewLocalState/NoteFormNewLocalState';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import NotesRepeater from 'src/components/NotesNewWithState/NotesRepeater';
import '../NoteFormNewLocalState/Notes.scss';

interface NotesWithStateProps {
  notes: any[];
  onSaveNote: (note: any) => void;
  onDeleteNote: (note: any) => void;
  onEditNote: any;
  breadcrumb?: any;
  from?: string;
}

const NotesWithState = ({
  notes,
  onSaveNote,
  onDeleteNote,
  onEditNote,
  breadcrumb,
  from,
}: NotesWithStateProps) => {
  const user = useSelector(selectAuthenticatedUser);

  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [note, setNote] = useState<any>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [editingNote, setEditingNote] = useState<any>(null);

  const displayNotes = useMemo(() => {
    return filterNotesBySearchTerm(notes, searchTerm);
  }, [notes, searchTerm]);

  const handleSearch = (searchValue: any): void => {
    const value =
      typeof searchValue === 'string'
        ? searchValue
        : searchValue?.target?.value || '';
    setSearchTerm(value);
  };

  const onImageClick = (x: number, data: any): void => {
    if (getUrlExtension(data.image) === 'heic') {
      window.open(data.image);
    } else {
      setIsOpen(true);
      setNote(data);
    }
  };

  const handleEditNote = (noteToEdit: any): void => {
    if (noteToEdit.appointment) {
      let date: Date = new Date(noteToEdit.appointment.date);
      let time: Date =
        noteToEdit.appointment && noteToEdit.appointment.time
          ? new Date(noteToEdit.appointment.time)
          : null;
      let appointment = {
        date,
        time,
      };
      noteToEdit = { ...noteToEdit, appointment };
    }

    setEditingNote(noteToEdit);
    setShowOverlay(true);
  };

  const handleDeleteNote = (noteToDelete: any): void => {
    if (
      window.confirm(`Are you sure you want to delete ${noteToDelete.title}?`)
    ) {
      onDeleteNote(noteToDelete);
    }
  };

  const handleCloseOverlay = (): void => {
    setShowOverlay(false);
    setEditingNote(null);
  };

  const handleSaveAndClose = (note: any) => {
    onSaveNote(note);
    handleCloseOverlay();
  };

  const handleAddNewNote = (): void => {
    setEditingNote(null);
    setShowOverlay(true);
  };

  const canAddNotes = !(
    user.roles[0].role.name === 'Strata Manager' ||
    user.roles[0].role.name === 'Vendor' ||
    user.roles[0].role.name === 'Home Owner' ||
    user.roles[0].role.name === 'Realtor' ||
    user.roles[0].role.name === 'Occupant'
  );

  return (
    <div className="notes">
      <Actions compact>
        <Search
          size="small"
          placeholder="Search Notes..."
          onChange={handleSearch}
          theme={undefined}
        />
        {canAddNotes && (
          <Button
            color="teal"
            label="Add Note"
            onClick={() => setShowOverlay(true)}
            icon={<FontAwesomeIcon glyph="edit" weight="light" size="small" />}
            size="small"
          />
        )}
      </Actions>
      {displayNotes.length > 0 ? (
        <>
          <NotesRepeater
            user={user}
            notes={displayNotes}
            onEdit={handleEditNote}
            onDelete={handleDeleteNote}
            onImageClicked={onImageClick}
            enableEdit={true}
          />
          {isOpen && (
            <ReusableLightbox
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
              image={note.image}
            />
          )}
        </>
      ) : (
        <Empty
          headline="No notes yet"
          strapline="You can use the Notes section to post any status updates or general comments to keep everyone on the same page."
          searchIsActive={searchTerm}
          cta={
            canAddNotes ? (
              <Button
                color="teal"
                label="Add Note"
                onClick={() => handleAddNewNote}
                icon={
                  <FontAwesomeIcon glyph="edit" weight="light" size="small" />
                }
                size="small"
              />
            ) : null
          }
          image={<img alt="No notes yet" src="/assets/empty/notes.svg" />}
        />
      )}
      <Overlay
        showOverlay={showOverlay}
        content={
          <NoteFormWithState
            from={from}
            breadcrumb={breadcrumb}
            onSaveNote={handleSaveAndClose}
            onClose={handleCloseOverlay}
            onEditNote={onEditNote}
            initialNote={editingNote}
          />
        }
      />
    </div>
  );
};

export default NotesWithState;
